import { type LucideIcon, Monitor, Moon, Sun } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { type ThemePreference, useTheme } from '../../providers/ThemeProvider'

/**
 * ThemeToggle — tri-state segmented control (light / dark / system).
 *
 * **Requires a `<ThemeProvider>` ancestor.** Calls `useTheme()` internally.
 *
 * @example
 * ```tsx
 * import { ThemeProvider, ThemeToggle } from '@intelliauth/ui'
 *
 * function App() {
 *   return (
 *     <ThemeProvider>
 *       <Nav><ThemeToggle /></Nav>
 *       <Main />
 *     </ThemeProvider>
 *   )
 * }
 * ```
 *
 * - `light` forces light, `dark` forces dark, `system` follows OS.
 * - Selecting any option also writes the `ia_theme_hint` cookie so the
 *   logged-out login screen remembers the last visual theme.
 */
export interface ThemeToggleProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Compact (icons only) vs labelled. Defaults to compact. */
  variant?: 'compact' | 'labelled'
}

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; Icon: LucideIcon }> = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
] as const

export const ThemeToggle = React.forwardRef<HTMLDivElement, ThemeToggleProps>(
  ({ className, variant = 'compact', ...rest }, ref) => {
    const { preference, setPreference } = useTheme()

    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label="Theme"
        className={cn(
          'inline-flex items-center gap-0.5 p-0.5 rounded-md border border-ia-border bg-card',
          className,
        )}
        {...rest}
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const active = preference === value
          // labelled: visible text is the accessible name → no aria-label
          // compact: no visible text → aria-label provides the name
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={variant === 'compact' ? label : undefined}
              onClick={() => setPreference(value)}
              className={cn(
                'inline-flex items-center justify-center gap-1.5 h-7 px-2 rounded-sm text-body-sm transition-colors',
                active
                  ? 'bg-ia-surface-hover text-ia-text'
                  : 'text-ia-text-tertiary hover:text-ia-text hover:bg-ia-surface-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
              {variant === 'labelled' && <span>{label}</span>}
            </button>
          )
        })}
      </div>
    )
  },
)
ThemeToggle.displayName = 'ThemeToggle'
